<template>
  <section class="appraisal-page">
    <header class="appraisal-hero">
      <div class="hero-content">
        <p>智能开放鉴定系统</p>
        <h2>让档案开放审核<br />更智能、更高效、更合规</h2>
        <p class="hero-description">面向海量、多类型档案开放审核场景，融合智能内容理解、规则研判与协同审核能力，帮助档案馆缩短鉴定周期、统一业务标准、降低人工漏审风险。</p>
        <div class="hero-tags">
          <span>智能识别</span>
          <span>规则研判</span>
          <span>协同审核</span>
          <span>安全可控</span>
        </div>
      </div>
      <div class="hero-panel" aria-hidden="true">
        <div class="panel-title"><span></span>开放鉴定工作台</div>
        <div class="panel-task"><strong>待审核档案</strong><span>1,286 件</span></div>
        <div class="panel-analysis"><span>内容智能解析</span><strong>已完成</strong></div>
        <div class="panel-warning"><span>风险内容预警</span><strong>12 处待复核</strong></div>
        <div class="panel-result"><span>开放建议</span><strong>可开放</strong></div>
        <div class="panel-trace">审核意见与处理记录已留痕</div>
      </div>
    </header>

    <section class="pain-section">
      <div class="section-heading">
        <h3>开放鉴定，面临多重挑战</h3>
        <p>面对存量档案集中开放需求，传统依赖人工筛查的方式难以兼顾效率、标准与安全。</p>
      </div>
      <div class="pain-list">
        <article v-for="item in painPoints" :key="item.title" class="pain-item">
          <h4>{{ item.title }}</h4>
          <p>{{ item.description }}</p>
        </article>
      </div>
    </section>

    <section class="ai-section">
      <div class="section-heading">
        <h3>核心 AI 智能能力</h3>
        <p>从档案内容解析到审核结论沉淀，让机器承担重复识别工作，让人员聚焦关键判断。</p>
      </div>
      <div class="capability-list">
        <article v-for="capability in aiCapabilities" :key="capability.title" class="capability-card">
          <h4>{{ capability.title }}</h4>
          <p>{{ capability.description }}</p>
          <ul>
            <li v-for="point in capability.points" :key="point">{{ point }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="process-section">
      <div class="section-heading process-heading">
        <h3>标准化业务闭环，推动审核结果高效落地</h3>
        <p>贯通智能预判、人工研判、协同审定和开放发布，确保每一份结论都有依据、可复核、能追溯。</p>
      </div>
      <div class="process-list">
        <article v-for="step in process" :key="step.title" class="process-step">
          <h4>{{ step.title }}</h4>
          <p>{{ step.description }}</p>
        </article>
      </div>
    </section>

    <section class="platform-section">
      <div class="section-heading">
        <h3>平台保障，兼顾信创适配与安全合规</h3>
        <p>适配政务档案工作环境，让智能开放鉴定能力安全、稳定地融入既有业务体系。</p>
      </div>
      <div class="platform-list">
        <article v-for="capability in platformCapabilities" :key="capability.title" class="platform-card">
          <h4>{{ capability.title }}</h4>
          <p>{{ capability.description }}</p>
          <ul>
            <li v-for="point in capability.points" :key="point">{{ point }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="intelligence-section">
      <div class="intelligence-copy">
        <h3>多模态智能理解，适应不同年代与类型档案</h3>
        <p>支持文本、图像、音频、视频等多种档案格式，结合深度学习、预训练模型与学习算法，提升内容识别和风险研判的准确性。</p>
        <div class="mode-list">
          <div v-for="mode in multimodalModes" :key="mode.title" class="mode-item">
            <strong>{{ mode.title }}</strong>
            <span>{{ mode.description }}</span>
          </div>
        </div>
      </div>
      <div class="knowledge-assets">
        <h3>让鉴定经验成为可复用的知识资产</h3>
        <article v-for="asset in knowledgeAssets" :key="asset.title" class="asset-card">
          <h4>{{ asset.title }}</h4>
          <p>{{ asset.description }}</p>
          <ul>
            <li v-for="point in asset.points" :key="point">{{ point }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="deployment-section">
      <div class="section-heading">
        <h3>本地化部署，保障档案数据安全可控</h3>
        <p>支持私有化部署和信创机房环境，档案原文无需上传云端，满足档案数据安全管理与合规审计要求。</p>
      </div>
      <div class="deployment-list">
        <article v-for="item in deploymentCapabilities" :key="item.title" class="deployment-item">
          <strong>{{ item.title }}</strong>
          <span>{{ item.description }}</span>
        </article>
      </div>
    </section>

    <section class="value-section">
      <div class="value-copy">
        <h3>从人工筛查到智能协同，让开放鉴定更有依据</h3>
        <p>以规则、案例和全流程记录支撑档案开放决策，持续提升审核效率与鉴定质量。</p>
      </div>
      <div class="value-list">
        <div v-for="value in values" :key="value.title" class="value-item">
          <strong>{{ value.title }}</strong>
          <span>{{ value.description }}</span>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
export default {
  name: 'OpenIdentification',
  data () {
    return {
      painPoints: [
        { title: '海量档案积压', description: '存量档案集中开放，人工逐件审核压力持续增加。' },
        { title: '鉴定周期较长', description: '内容筛查、意见研判与台账整理占用大量工作时间。' },
        { title: '专业人才不足', description: '复杂档案鉴定依赖经验人员，审核资源难以规模化复制。' },
        { title: '业务标准不一', description: '不同门类、地区和行业要求，需要统一沉淀并灵活执行。' },
        { title: '档案格式多样', description: '文本、图像、音视频内容并存，识别与研判难度更高。' },
        { title: '安全审计要求高', description: '未鉴定档案、涉敏信息与审核结论均需严格管控和追溯。' }
      ],
      aiCapabilities: [
        {
          title: 'OCR 与 NLP 智能解析',
          description: '融合文字识别与语义理解能力，快速获取档案内容与关键元数据。',
          points: ['自动提取档案题名、时间、人物等信息', '辅助形成结构化著录和开放初审依据']
        },
        {
          title: '多模态内容理解',
          description: '面向不同类型档案统一开展内容分析，提高复杂资料的识别覆盖度。',
          points: ['支持文本、图像、音频、视频等格式', '支持跨模态内容关联与风险辅助研判']
        },
        {
          title: '敏感风险智能预警',
          description: '针对涉敏片段主动定位、标记和预警，降低人工漏审隐患。',
          points: ['自定义敏感词、隐私信息和涉密术语规则', '按风险类型展示命中内容和处理建议']
        },
        {
          title: '经验复用与协同审核',
          description: '将历史结论与专家经验沉淀为可复用依据，支持规范化协同审定。',
          points: ['参考历史规则、划控意见与相似案例', '支持原文批注、审核留痕、台账与报表自动生成']
        }
      ],
      process: [
        { title: '智能解析', description: '自动识别档案文本、图像、音视频内容，提取元数据并完成初步理解。' },
        { title: '规则研判', description: '结合规则库、敏感词库和案例库，定位风险内容并生成鉴定建议。' },
        { title: '批注审核', description: '初审、复审、审定人员查看依据、在线批注并完成协同研判。' },
        { title: '开放发布', description: '生成鉴定台账与审计记录，对接现有平台并支持开放目录一键发布。' }
      ],
      platformCapabilities: [
        {
          title: '国产化全栈适配',
          description: '适配党政档案馆信创改造与国产化替代建设环境。',
          points: ['支持银河麒麟、统信 UOS 等国产操作系统', '兼容人大金仓、达梦等国产数据库和中间件环境', '支持信创机房与私有化部署']
        },
        {
          title: '等保合规安全体系',
          description: '按照网络安全等级保护要求设计，保障档案资源和审核过程安全。',
          points: ['角色分级授权，初审复审审定岗位数据隔离', '操作日志、审批记录和修改痕迹全程可追溯', '加强传输、存储与访问控制，防范信息外泄']
        },
        {
          title: '标准化业务支撑',
          description: '将开放鉴定细则转化为可配置、可执行的业务闭环。',
          points: ['配置审批节点、表单模板和公示材料', '适配各地档案管理细则与行业特殊规定', '对接数字档案管理和查阅服务平台']
        }
      ],
      multimodalModes: [
        { title: '文本理解', description: '识别正文、表格与关键信息' },
        { title: '图像识别', description: '分析扫描件、照片与图像资料' },
        { title: '音频处理', description: '辅助解析录音等声像档案内容' },
        { title: '视频处理', description: '支持视频资料的内容理解与检索' }
      ],
      knowledgeAssets: [
        {
          title: '规则库',
          description: '将国家标准、地方规范和单位制度转化为可执行规则。',
          points: ['国家档案局相关标准', '地方鉴定规范与行业特殊规定', '支持自定义规则持续完善']
        },
        {
          title: '敏感词库',
          description: '统一维护多类涉敏信息，为风险识别提供持续更新的依据。',
          points: ['政治敏感词与涉密术语', '隐私、商业敏感等风险词', '支持自定义词库与规则联动']
        },
        {
          title: '案例库',
          description: '沉淀历史鉴定结论与专家经验，推动同类档案快速参考。',
          points: ['历史鉴定案例和划控意见', '典型案例学习与经验知识化', '随业务开展持续扩充']
        }
      ],
      deploymentCapabilities: [
        { title: '私有化部署', description: '档案数据无需上传云端，满足本地化管理要求。' },
        { title: '审计追溯机制', description: '完整记录操作、审批、批注与鉴定意见，支撑合规检查。' },
        { title: '标准规范遵循', description: '遵循国家档案相关规定，持续满足业务与安全合规要求。' }
      ],
      values: [
        { title: '提升效率', description: '减少重复筛查，缓解海量档案积压压力' },
        { title: '统一标准', description: '以规则与案例沉淀稳定、可执行的鉴定依据' },
        { title: '降低成本', description: '减少人工研判、统计与重复审核工作量' }
      ]
    }
  }
}
</script>

<style scoped>
.appraisal-page { overflow: hidden; background: #fff; color: #20344e; }
.appraisal-hero { display: flex; min-height: 460px; align-items: center; justify-content: space-between; gap: 70px; padding: 76px max(24px, calc((100vw - 1200px) / 2)); background: #f4f8ff; }
.hero-content { max-width: 640px; }
.hero-content > p:first-child { margin: 0 0 15px; color: #1f5dea; font-size: 13px; font-weight: 600; }
.hero-content h2 { margin: 0; color: #20344e; font-size: 40px; font-weight: 600; line-height: 1.45; }
.hero-description { max-width: 590px; margin: 22px 0 0; color: #6c7d91; font-size: 16px; line-height: 1.9; }
.hero-tags { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 28px; }
.hero-tags span { padding: 8px 16px; border: 1px solid #d7e4fb; border-radius: 3px; background: #fff; color: #3866a9; font-size: 13px; }
.hero-panel { width: 330px; flex: 0 0 auto; padding: 28px; border: 1px solid #dce8f8; border-radius: 6px; background: #fff; box-shadow: 0 16px 35px rgba(49, 91, 147, .1); }
.panel-title { display: flex; align-items: center; gap: 9px; color: #38506d; font-size: 15px; font-weight: 600; }
.panel-title span { width: 8px; height: 8px; border-radius: 50%; background: #2e73e8; }
.panel-task, .panel-analysis, .panel-warning, .panel-result { display: flex; align-items: center; justify-content: space-between; }
.panel-task { margin-top: 26px; padding: 13px 14px; background: #f4f8ff; color: #637995; font-size: 12px; }
.panel-task strong { color: #38506d; font-weight: 500; }
.panel-task span { color: #2e73e8; font-size: 15px; font-weight: 600; }
.panel-analysis, .panel-warning { margin-top: 17px; padding-bottom: 13px; border-bottom: 1px solid #eef2f8; color: #75869a; font-size: 12px; }
.panel-analysis strong { color: #23a47a; font-weight: 500; }
.panel-warning strong { color: #d07832; font-weight: 500; }
.panel-result { margin-top: 20px; color: #75869a; font-size: 13px; }
.panel-result strong { color: #2e73e8; font-weight: 600; }
.panel-trace { margin-top: 21px; padding-top: 15px; border-top: 1px solid #eef2f8; color: #8c9bae; font-size: 11px; }
.pain-section, .ai-section, .platform-section, .deployment-section { padding: 78px max(24px, calc((100vw - 1200px) / 2)); }
.section-heading { max-width: 700px; margin: 0 auto 46px; text-align: center; }
.section-heading h3, .intelligence-copy h3, .knowledge-assets > h3, .value-copy h3 { margin: 0; color: #20344e; font-size: 30px; font-weight: 600; line-height: 1.45; }
.section-heading > p:last-child { margin: 16px 0 0; color: #8290a1; font-size: 15px; line-height: 1.8; }
.pain-section { background: #fff; }
.pain-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.pain-item { padding: 22px 24px; border-left: 3px solid #d9e7fb; background: #f8fbff; }
.pain-item h4, .capability-card h4, .platform-card h4, .asset-card h4 { margin: 0; color: #263e5d; font-size: 18px; font-weight: 600; line-height: 1.5; }
.pain-item p { margin: 9px 0 0; color: #76869a; font-size: 13px; line-height: 1.7; }
.ai-section { background: #f7f9fc; }
.capability-list { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
.capability-card, .platform-card { display: flex; min-height: 292px; flex-direction: column; padding: 28px 24px; border: 1px solid #e4ebf4; border-radius: 5px; background: #fff; transition: transform .2s ease, box-shadow .2s ease; }
.capability-card:hover, .platform-card:hover, .asset-card:hover { transform: translateY(-4px); box-shadow: 0 12px 25px rgba(43, 82, 132, .1); }
.capability-card > p, .platform-card > p { min-height: 66px; margin: 13px 0 0; color: #76869a; font-size: 13px; line-height: 1.7; }
.capability-card ul, .platform-card ul, .asset-card ul { margin: 17px 0 0; padding: 14px 0 0 17px; border-top: 1px solid #edf1f6; color: #59718f; }
.capability-card li, .platform-card li, .asset-card li { margin-bottom: 7px; font-size: 13px; line-height: 1.55; }
.process-section { padding: 78px max(24px, calc((100vw - 1200px) / 2)); background: #fff; }
.process-heading { max-width: 760px; margin-bottom: 42px; }
.process-list { display: grid; grid-template-columns: repeat(4, 1fr); }
.process-step { position: relative; padding: 0 27px 0 0; }
.process-step:not(:last-child)::after { position: absolute; top: 14px; right: 12px; width: 1px; height: 62px; background: #d9e7fb; content: ''; }
.process-step h4 { margin: 0 0 12px; color: #2b4564; font-size: 18px; font-weight: 600; }
.process-step p { max-width: 220px; margin: 0; color: #77879a; font-size: 14px; line-height: 1.8; }
.platform-section { background: #f7f9fc; }
.platform-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.platform-card { min-height: 310px; }
.platform-card > p { min-height: 44px; }
.intelligence-section { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, .95fr); gap: 70px; padding: 78px max(24px, calc((100vw - 1200px) / 2)); background: #fff; }
.intelligence-copy > p { max-width: 570px; margin: 18px 0 0; color: #76869a; font-size: 15px; line-height: 1.9; }
.mode-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; margin-top: 30px; border: 1px solid #e1eaf5; background: #e1eaf5; }
.mode-item { min-height: 97px; padding: 19px 20px; background: #fff; }
.mode-item strong, .mode-item span { display: block; }
.mode-item strong { color: #2e73e8; font-size: 16px; font-weight: 600; }
.mode-item span { margin-top: 8px; color: #76869a; font-size: 12px; line-height: 1.6; }
.knowledge-assets { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; align-content: start; }
.knowledge-assets > h3 { grid-column: 1 / -1; margin-bottom: 17px; font-size: 25px; }
.asset-card { min-height: 248px; padding: 20px 17px; border: 1px solid #e4ebf4; border-radius: 5px; background: #f8fbff; transition: transform .2s ease, box-shadow .2s ease; }
.asset-card h4 { font-size: 16px; }
.asset-card > p { min-height: 59px; margin: 11px 0 0; color: #76869a; font-size: 12px; line-height: 1.7; }
.asset-card ul { margin-top: 13px; padding-top: 11px; }
.asset-card li { font-size: 12px; }
.deployment-section { background: #eff6ff; }
.deployment-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; border: 1px solid #d8e7f9; background: #fff; }
.deployment-item { padding: 27px 28px; border-right: 1px solid #d8e7f9; }
.deployment-item:last-child { border-right: 0; }
.deployment-item strong, .deployment-item span { display: block; }
.deployment-item strong { color: #2e73e8; font-size: 19px; font-weight: 600; }
.deployment-item span { margin-top: 11px; color: #718399; font-size: 13px; line-height: 1.75; }
.value-section { display: flex; align-items: center; justify-content: space-between; gap: 80px; padding: 75px max(24px, calc((100vw - 1200px) / 2)); background: #fff; }
.value-copy { max-width: 450px; }
.value-copy > p { margin: 18px 0 0; color: #76869a; font-size: 15px; line-height: 1.9; }
.value-list { display: flex; min-width: 520px; border-top: 1px solid #e2eaf3; border-bottom: 1px solid #e2eaf3; }
.value-item { flex: 1; padding: 22px 20px; border-right: 1px solid #e2eaf3; }
.value-item:first-child { padding-left: 0; }
.value-item:last-child { border-right: 0; }
.value-item strong, .value-item span { display: block; }
.value-item strong { color: #2e73e8; font-size: 19px; font-weight: 600; }
.value-item span { margin-top: 9px; color: #7d8c9e; font-size: 12px; line-height: 1.6; }
@media (max-width: 1000px) {
  .appraisal-hero { gap: 38px; }
  .hero-content h2 { font-size: 34px; }
  .pain-list { grid-template-columns: repeat(2, 1fr); }
  .capability-list { grid-template-columns: repeat(2, 1fr); }
  .platform-list { grid-template-columns: repeat(2, 1fr); }
  .intelligence-section { gap: 42px; }
  .knowledge-assets { grid-template-columns: 1fr; }
  .knowledge-assets > h3 { margin-bottom: 5px; }
  .asset-card { min-height: 0; }
  .asset-card > p { min-height: 0; }
  .value-section { gap: 40px; }
  .value-list { min-width: 0; }
}
@media (max-width: 650px) {
  .appraisal-hero { display: block; min-height: 0; padding: 58px 24px; }
  .hero-content h2 { font-size: 29px; }
  .hero-description { font-size: 14px; }
  .hero-panel { width: auto; margin-top: 36px; }
  .pain-section, .ai-section, .platform-section, .deployment-section, .process-section { padding: 58px 20px; }
  .section-heading { margin-bottom: 32px; }
  .section-heading h3, .intelligence-copy h3, .knowledge-assets > h3, .value-copy h3 { font-size: 25px; }
  .section-heading > p:last-child { font-size: 14px; }
  .pain-list, .capability-list, .platform-list { grid-template-columns: 1fr; gap: 14px; }
  .pain-item { padding: 20px; }
  .capability-card, .platform-card { min-height: 0; padding: 24px 20px; }
  .capability-card > p, .platform-card > p { min-height: 0; }
  .process-list { grid-template-columns: 1fr; gap: 0; }
  .process-step { padding: 0 0 25px 20px; border-left: 1px solid #d9e7fb; }
  .process-step:not(:last-child)::after { display: none; }
  .process-step:last-child { padding-bottom: 0; }
  .process-step h4 { font-size: 17px; }
  .process-step p { max-width: none; font-size: 13px; }
  .intelligence-section { display: block; padding: 58px 24px; }
  .intelligence-copy > p { font-size: 14px; }
  .mode-list { grid-template-columns: 1fr; }
  .knowledge-assets { display: grid; grid-template-columns: 1fr; margin-top: 48px; }
  .knowledge-assets > h3 { margin-bottom: 5px; }
  .deployment-list { grid-template-columns: 1fr; }
  .deployment-item { padding: 22px 20px; border-right: 0; border-bottom: 1px solid #d8e7f9; }
  .deployment-item:last-child { border-bottom: 0; }
  .value-section { display: block; padding: 58px 24px; }
  .value-list { display: block; min-width: 0; margin-top: 30px; }
  .value-item, .value-item:first-child { padding: 18px 0; border-right: 0; border-bottom: 1px solid #e2eaf3; }
  .value-item:last-child { border-bottom: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .capability-card, .platform-card, .asset-card { transition: none; }
}
</style>
